import { useQuery } from '@tanstack/react-query';
import { useState } from 'react';
import { api, clearAdminToken, getAdminToken, setAdminToken } from './lib/api';
import { Link, Routes, type RouteDefinition } from './lib/router';
import { AccountsPage } from './pages/AccountsPage';
import { AddAdvertPage } from './pages/AddAdvertPage';
import { AdvertListPage } from './pages/AdvertListPage';
import { LimitsPage } from './pages/LimitsPage';

const NAV = [
  { to: '/adverts', label: 'Adverts' },
  { to: '/adverts/new', label: 'Add advert' },
  { to: '/limits', label: 'Limits' },
  { to: '/accounts', label: 'Accounts' },
];

const ROUTES: RouteDefinition[] = [
  { path: '/adverts', element: <AdvertListPage /> },
  { path: '/adverts/new', element: <AddAdvertPage /> },
  { path: '/limits', element: <LimitsPage /> },
  { path: '/accounts', element: <AccountsPage /> },
];

export function App() {
  const [hasToken, setHasToken] = useState(() => Boolean(getAdminToken()));

  if (!hasToken) return <TokenGate onSaved={() => setHasToken(true)} />;

  return (
    <div className="min-h-screen">
      <Header
        onSignOut={() => {
          clearAdminToken();
          setHasToken(false);
        }}
      />
      <main className="mx-auto max-w-7xl px-4 py-8 sm:px-6 lg:px-8">
        <Routes routes={ROUTES} fallback="/adverts" />
      </main>
    </div>
  );
}

function Header({ onSignOut }: { onSignOut: () => void }) {
  const { data: stats } = useQuery({ queryKey: ['dashboard'], queryFn: api.dashboard });

  return (
    <header className="sticky top-0 z-20 border-b border-border-subtle bg-surface/85 backdrop-blur">
      <div className="mx-auto flex max-w-7xl items-center gap-6 px-4 py-3 sm:px-6 lg:px-8">
        <div className="flex items-baseline gap-2">
          <span className="text-lg font-semibold tracking-tight">ADVControl</span>
          <span className="text-xs text-slate-500">OLX.bg</span>
        </div>

        <nav className="flex items-center gap-1">
          {NAV.map((item) => (
            <Link
              key={item.to}
              to={item.to}
              className="rounded-lg px-3 py-1.5 text-sm font-medium transition-colors"
              activeClassName="bg-accent-soft text-white"
              inactiveClassName="text-slate-400 hover:bg-surface-3 hover:text-slate-200"
            >
              {item.label}
            </Link>
          ))}
        </nav>

        <div className="ml-auto flex items-center gap-4 text-xs text-slate-500">
          {stats ? (
            <>
              <span title="Authorized accounts / total enabled">
                accounts <span className="text-slate-300">{stats.accounts.authorized}</span>/{stats.accounts.total}
              </span>
              <span title="Active adverts">
                active <span className="text-slate-300">{stats.adverts.active ?? 0}</span>
              </span>
              {stats.failedPublishes > 0 ? (
                <span className="text-red-400" title="Failed publish attempts">
                  failed {stats.failedPublishes}
                </span>
              ) : null}
            </>
          ) : null}
          <button type="button" className="text-slate-400 hover:text-slate-200" onClick={onSignOut}>
            Sign out
          </button>
        </div>
      </div>
    </header>
  );
}

/**
 * The whole API requires ADMIN_API_TOKEN. Rather than shipping a login system for
 * a two-operator internal tool, the token is entered once and kept in
 * localStorage.
 */
function TokenGate({ onSaved }: { onSaved: () => void }) {
  const [value, setValue] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [checking, setChecking] = useState(false);

  const submit = async (event: React.FormEvent) => {
    event.preventDefault();
    setChecking(true);
    setError(null);
    setAdminToken(value);

    try {
      await api.dashboard();
      onSaved();
    } catch {
      clearAdminToken();
      setError('That token was rejected. Check ADMIN_API_TOKEN in the backend environment.');
    } finally {
      setChecking(false);
    }
  };

  return (
    <div className="grid min-h-screen place-items-center px-4">
      <form onSubmit={submit} className="card w-full max-w-md p-8">
        <h1 className="text-lg font-semibold">ADVControl</h1>
        <p className="mt-1 mb-6 text-sm text-slate-400">Enter the admin API token to continue.</p>

        <label className="label" htmlFor="token">
          Admin API token
        </label>
        <input
          id="token"
          type="password"
          className="input"
          value={value}
          onChange={(event) => setValue(event.target.value)}
          placeholder="••••••••••••••••"
          autoComplete="off"
          required
        />

        {error ? <p className="mt-3 text-sm text-red-300">{error}</p> : null}

        <button type="submit" className="btn-primary mt-6 w-full" disabled={checking || value.length < 8}>
          {checking ? <Spinner /> : null}
          Continue
        </button>
      </form>
    </div>
  );
}

function Spinner() {
  return <span className="inline-block h-4 w-4 animate-spin rounded-full border-2 border-current border-t-transparent" />;
}
